/**
 * The shared primitives.
 *
 * Small and unstyled-by-default on purpose: every screen composes these
 * rather than hand-rolling a button, so a change to the hover treatment lands
 * everywhere at once.
 */

import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState,
  type ComponentType, type ReactNode, useId } from 'react';
import { Tip } from './HoverCard';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { useBackdropClose } from '@/lib/backdrop';
import { Icon } from '@/icons/Icon';
import { Switch } from './Switch';

/* ------------------------------------------------------------ button */

type ButtonKind = 'primary' | 'outline' | 'ghost' | 'danger' | 'plain';

/**
 * A ripple that starts where you actually clicked.
 *
 * The span is added and removed by hand rather than held in state: a click
 * should not re-render the button, and two quick clicks should overlap rather
 * than the second replacing the first.
 */
function ripple(e: React.PointerEvent<HTMLElement>) {
  if (document.body.classList.contains('reduce-motion')) return;
  const host = e.currentTarget;
  const box = host.getBoundingClientRect();
  // Big enough to reach the far corner from wherever it started.
  const size = Math.hypot(box.width, box.height) * 2;

  const dot = document.createElement('span');
  dot.className = 'pointer-events-none absolute rounded-full';
  dot.style.cssText =
    'width:' + size + 'px;height:' + size + 'px;' +
    'left:' + (e.clientX - box.left - size / 2) + 'px;' +
    'top:' + (e.clientY - box.top - size / 2) + 'px;' +
    'background:currentColor;opacity:.22;transform:scale(0);' +
    'animation:ripple 520ms var(--ease-out-soft) forwards';
  host.appendChild(dot);
  setTimeout(() => dot.remove(), 540);
}

export function Button({
  kind = 'plain', size = 'md', icon, className, children, ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  kind?: ButtonKind;
  size?: 'sm' | 'md';
  icon?: ReactNode;
}) {
  /* data-kind is for the stylesheet: a scene gives the one primary action
     on the screen its glow without having to guess at class names. */
  return (
    <button
      type="button"
      {...rest}
      data-kind={kind}
      onPointerDown={(e) => {
        /* The ripple is decoration; someone who asked for less motion does
           not get it. The press state below still shows immediately. */
        if (!prefersLessMotion()) ripple(e);
        rest.onPointerDown?.(e);
      }}
      className={cn(
        /*
         * One shape for every button in the app.
         *
         * This used to be a glossy gradient pill with a glow on hover, while
         * every newer screen used a flat indigo rectangle - two primary styles
         * side by side, which is the first thing the review called out. Flat,
         * 12px corners, the same height as inputs, and colour that fades
         * rather than flashes.
         */
        'relative inline-flex select-none items-center justify-center gap-2 overflow-hidden whitespace-nowrap',
        'rounded-[calc(12px*var(--radius-mul,1))] border font-bold tracking-[-.005em] outline-none',
        'transition-[background-color,border-color,color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)]',
        /* Press feedback is immediate and small - no bounce. */
        'active:scale-[.985] [transition:background-color_var(--t-hover)_var(--ease),border-color_var(--t-hover)_var(--ease),color_var(--t-hover)_var(--ease),box-shadow_var(--t-hover)_var(--ease),transform_var(--t-press)_var(--ease)]',
        'disabled:pointer-events-none disabled:opacity-45',
        'focus-visible:shadow-[0_0_0_2px_var(--bg-1),0_0_0_4px_var(--accent)]',
        size === 'sm' ? 'h-[var(--h-sm)] px-3 text-[12.5px]' : 'h-[var(--h-md)] px-[18px] text-[14px]',

        kind === 'primary' && 'border-transparent bg-accent text-accent-fg hover:bg-accent-hi active:bg-accent-lo',
        kind === 'outline' && 'border-line bg-surface-1 text-dim hover:border-accent-line hover:text-ink',
        kind === 'ghost' && 'border-transparent bg-transparent text-mute hover:bg-surface-2 hover:text-ink',
        kind === 'danger' && 'border-transparent bg-bad text-white hover:bg-[color-mix(in_srgb,var(--bad)_88%,#000)]',
        kind === 'plain' && 'border-line bg-surface-2 text-ink hover:border-accent-line hover:bg-surface-3',
        className
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/** True when the system or the in-app setting asks for reduced motion. */
function prefersLessMotion(): boolean {
  return document.body.classList.contains('reduce-motion')
    || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function IconButton({
  label, className, children, ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  /*
   * The label shows in Pugo's own tooltip rather than the browser's.
   *
   * A title attribute gets the operating system tooltip - a grey box in a
   * different font that appears a second late and looks nothing like the app.
   */
  return (
    <Tip text={label}>
    <button
      type="button"
      aria-label={label}
      {...rest}
      className={cn(
        'grid h-[var(--h-sm)] w-[var(--h-sm)] flex-none place-items-center rounded-[var(--radius-s)]',
        'text-mute transition-[background,color,transform] duration-150 ease-[var(--ease-out-soft)]',
        'hover:bg-surface-3 hover:text-ink active:scale-[.9]',
        'disabled:pointer-events-none disabled:opacity-40',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-hi',
        className
      )}
    >
      {children}
    </button>
    </Tip>
  );
}

/* -------------------------------------------------------------- card */

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <section className={cn('pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-[var(--card-pad)] shadow-[inset_0_0_0_1px_var(--line-soft)] transition-shadow duration-[250ms] hover:shadow-[inset_0_0_0_1px_var(--line)]', className)}>
      {children}
    </section>
  );
}

export function CardHead({ title, note, actions }: { title: string; note?: string; actions?: ReactNode }) {
  return (
    <header className="mb-[calc(var(--gap)*.7)] flex flex-wrap items-center gap-3">
      <h3 className="text-[16.5px] font-bold tracking-[-.01em]">{title}</h3>
      <div className="flex-1" />
      {note ? <span className="text-[13px] text-mute">{note}</span> : null}
      {actions}
    </header>
  );
}

/**
 * A card header with a mark beside the title.
 *
 * The plain CardHead is a line of text, which is right for a settings page
 * where the heading is the only thing distinguishing one row of controls from
 * the next. On a profile the cards are read at a glance rather than in order,
 * and a small tinted glyph gives each one a shape you recognise before you
 * have read the word.
 */
export function CardTitle({ icon, title, note, actions, accent }: {
  icon: string;
  title: string;
  note?: string;
  actions?: ReactNode;
  /** Overrides the tint, for a card about something with its own colour. */
  accent?: string;
}) {
  return (
    <header className="mb-[calc(var(--gap)*.7)] flex items-center gap-2.5">
      <span
        className="grid h-7 w-7 flex-none place-items-center rounded-[calc(8px*var(--radius-mul,1))]"
        style={{
          background: 'color-mix(in srgb, ' + (accent || 'var(--accent)') + ' 14%, transparent)',
          color: accent || 'var(--accent-hi)'
        }}
      >
        <Icon name={icon} size={14} />
      </span>
      <h3 className="text-[14.5px] font-bold tracking-[-.01em]">{title}</h3>
      <div className="min-w-0 flex-1" />
      {note ? <span className="flex-none text-[12px] text-mute tabular-nums">{note}</span> : null}
      {actions}
    </header>
  );
}

/* -------------------------------------------------------------- bits */

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-2">
      {/* Sentence case at a readable size. The tiny tracked-out capitals read as
          decoration rather than as the name of the field, and the references and
          the brief both use ordinary labels. */}
      <span className="text-[13.5px] font-semibold text-ink">{label}</span>
      {children}
      {hint ? <span className="text-[12px] text-mute">{hint}</span> : null}
    </label>
  );
}

export function Input({ className, ...rest }: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...rest}
      className={cn(
        'h-[var(--h-md)] w-full rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-4 shadow-[inset_0_0_0_1px_var(--line)]',
        'outline-none transition-shadow duration-[250ms] placeholder:text-ghost focus:shadow-[inset_0_0_0_1px_var(--accent),0_0_0_3px_var(--accent-soft)]',
        className
      )}
    />
  );
}

export function Textarea({ className, ...rest }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...rest}
      className={cn(
        'min-h-[92px] w-full resize-none rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-3 shadow-[inset_0_0_0_1px_var(--line)]',
        'outline-none transition-shadow duration-[250ms] placeholder:text-ghost focus:shadow-[inset_0_0_0_1px_var(--accent),0_0_0_3px_var(--accent-soft)]',
        className
      )}
    />
  );
}

export function Toggle({
  checked, onChange, label, note, disabled
}: { checked: boolean; onChange: (next: boolean) => void; label: string; note?: string; disabled?: boolean }) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-4 rounded-[calc(10px*var(--radius-mul,1))] py-[var(--row-pad)] transition-colors',
        'hover:bg-[color-mix(in_srgb,var(--accent)_6%,transparent)]',
        disabled && 'pointer-events-none opacity-40'
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold">{label}</span>
        {note ? <span className="block text-[13.5px] text-mute">{note}</span> : null}
      </span>
      <Switch checked={checked} disabled={disabled} onChange={onChange} label={label} />
    </label>
  );
}

export function Tag({
  kind = 'plain', className, children
}: { kind?: 'plain' | 'good' | 'warn' | 'bad' | 'info' | 'accent' | 'gold'; className?: string; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex flex-none items-center gap-1 rounded-md px-1.5 py-0.5 text-[9.5px] font-extrabold tracking-[.07em]',
        kind === 'plain' && 'bg-surface-4 text-dim',
        kind === 'good' && 'bg-[color-mix(in_srgb,var(--good)_18%,transparent)] text-good',
        kind === 'warn' && 'bg-[color-mix(in_srgb,var(--warn)_18%,transparent)] text-warn',
        kind === 'bad' && 'bg-[color-mix(in_srgb,var(--bad)_16%,transparent)] text-bad',
        kind === 'info' && 'bg-[color-mix(in_srgb,var(--info)_16%,transparent)] text-info',
        kind === 'accent' && 'bg-[color-mix(in_srgb,var(--accent)_20%,transparent)] text-accent-hi',
        kind === 'gold' && 'bg-gradient-to-br from-[#ffd76a] to-[#ffb03a] text-[#241d05]',
        className
      )}
    >
      {children}
    </span>
  );
}

/**
 * An empty or failed state.
 *
 * With a place for actions, because an empty state that only describes the
 * problem is a dead end: "no parties match" should come with the button that
 * clears the filter, and "could not load" with the one that tries again.
 */
export function Empty({ title, note, className, icon, actions, role }: {
  title: string;
  note?: string;
  className?: string;
  icon?: ReactNode;
  actions?: ReactNode;
  /** 'alert' for failures, so assistive tech announces them. */
  role?: 'status' | 'alert';
}) {
  /* A failure gets the alert mark in the bad colour; anything else its own
     icon (a name or an element) in the accent. Never a bare line of text. */
  const failed = role === 'alert';
  const mark = typeof icon === 'string' ? <Icon name={icon} size={24} /> : icon ?? <Icon name={failed ? 'alert' : 'info'} size={24} />;
  return (
    <div
      role={role}
      className={cn(
        'pugo-panel grid place-items-center rounded-[calc(20px*var(--radius-mul,1))] bg-surface-1 px-6 py-12 text-center',
        failed ? 'shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--bad)_30%,var(--line-soft))]' : 'shadow-[inset_0_0_0_1px_var(--line-soft)]',
        className
      )}
    >
      <span className={cn(
        'mb-4 grid h-14 w-14 place-items-center rounded-[calc(16px*var(--radius-mul,1))]',
        failed ? 'bg-[color-mix(in_srgb,var(--bad)_14%,transparent)] text-bad' : 'bg-accent-soft text-accent'
      )}>
        {mark}
      </span>
      <h3 className="m-0 text-[17px] font-bold text-ink">{title}</h3>
      {note ? <p className="m-0 mt-1.5 max-w-[46ch] text-[14px] leading-relaxed text-mute">{note}</p> : null}
      {actions ? <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">{actions}</div> : null}
    </div>
  );
}

export type TabOption<T extends string> = {
  id: T;
  label: string;
  icon?: ComponentType<{ size?: number; className?: string }>;
  badge?: number;
};

/**
 * A strip of tabs. The selected pill slides between them rather than
 * appearing, so the eye follows the change instead of re-finding it.
 */
export function Tabs<T extends string>({
  value, onChange, options
}: { value: T; onChange: (next: T) => void; options: TabOption<T>[] }) {
  const wrap = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const host = wrap.current;
    const active = host?.querySelector<HTMLElement>('[data-on="true"]');
    if (!host || !active) return;
    setPill({ left: active.offsetLeft, width: active.offsetWidth });
  }, [value, options.length]);

  return (
    <div
      ref={wrap}
      // Full width with equal segments, so the strip reads as one control
      // spanning the page rather than a huddle of buttons on the left.
      className="relative flex w-full rounded-[calc(14px*var(--radius-mul,1))] border border-line-soft bg-surface-1 p-[5px]"
    >
      {pill ? (
        <span
          aria-hidden
          className="pointer-events-none absolute top-[5px] bottom-[5px] rounded-[calc(10px*var(--radius-mul,1))] bg-surface-2 shadow-[inset_0_1px_0_color-mix(in_srgb,#fff_5%,transparent)] transition-[left,width] duration-300 ease-[var(--ease-out-soft)]"
          style={{ left: pill.left, width: pill.width }}
        />
      ) : null}

      {options.map((o) => {
        const Icon = o.icon;
        const on = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            data-on={on}
            onClick={() => onChange(o.id)}
            className={cn(
              'relative z-[1] flex h-[40px] flex-1 items-center justify-center gap-2 rounded-[calc(10px*var(--radius-mul,1))] px-2 text-[13.5px] font-semibold',
              'transition-colors duration-200',
              on ? 'text-ink' : 'text-mute hover:text-dim'
            )}
          >
            {Icon ? (
              <Icon
                size={17}
                className={cn('flex-none transition-opacity duration-200', on ? 'opacity-100' : 'opacity-55')}
              />
            ) : null}
            <span className="truncate">{o.label}</span>
            {o.badge ? <Tag kind="bad">{o.badge}</Tag> : null}
            <span
              aria-hidden
              className={cn(
                // The same lit underline the navigation uses, so the two
                // rows of tabs read as the same kind of control.
                'absolute inset-x-6 bottom-[3px] h-[2.5px] origin-center rounded-full',
                'bg-[linear-gradient(90deg,transparent,var(--accent-hi),transparent)]',
                'shadow-[0_0_10px_var(--accent-hi),0_0_20px_var(--glow)]',
                'transition-transform duration-300 ease-[var(--ease-out-soft)]',
                on ? 'scale-x-100' : 'scale-x-0'
              )}
            />
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------- modal */

export function Modal({
  title, subtitle, wide, xl, side, onClose, footer, children
}: {
  title: string;
  subtitle?: string;
  wide?: boolean;
  /** Room for a form with a preview beside it. */
  xl?: boolean;
  /**
   * Docked to the right edge, full height, rather than floating in the middle.
   *
   * For the dialogs that are really a side panel on the page you are on -
   * adding a friend while reading your friends list - where a box in the
   * middle of the screen covers the very thing it is about.
   */
  side?: boolean;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const backdrop = useBackdropClose(onClose);
  const boxRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  /*
   * Focus goes into the dialog, stays there, and comes back.
   *
   * Every dialog in the app used to open with focus left on whatever was
   * behind it, so Tab walked the page underneath and a screen reader had no
   * idea anything had opened. Focus lands on the dialog itself rather than its
   * first field: on a phone, focusing an input throws the keyboard up over the
   * very form you are trying to read.
   */
  useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    /* A dialog that autofocuses its own field has already put focus inside by
       now - leave it there rather than pulling it back to the box. */
    if (!boxRef.current?.contains(document.activeElement)) boxRef.current?.focus();
    return () => { back?.focus?.(); };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }

      if (e.key === 'Tab' && boxRef.current) {
        const focusable = [...boxRef.current.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
        )].filter((el) => el.offsetParent !== null || el === document.activeElement);
        if (!focusable.length) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === boxRef.current)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
        return;
      }

      // Enter takes the main action, unless you are typing something
      // multi-line or a menu inside the dialog is open.
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        const target = e.target as HTMLElement;
        if (target.tagName === 'TEXTAREA' || target.isContentEditable || target.tagName === 'BUTTON') return;
        /* A field that handles its own Enter - naming a saved setup, say -
           must not also fire the dialog's main action. */
        if (target.closest('[data-enter-own]')) return;
        const primary = boxRef.current?.querySelector<HTMLButtonElement>('[data-primary]:not(:disabled)');
        if (!primary) return;
        e.preventDefault();
        primary.click();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return createPortal(
    <div
      // Marked so the layers underneath - the profile card, a menu - treat a
      // click in here as inside themselves. Without it, opening this dialog
      // from a popout meant the first click on any field closed the popout
      // and took the dialog with it.
      data-keeps-card
      /* A sheet from the bottom on phones, a centred box from 640px up. */
      className={cn(
        'fixed inset-0 z-[300] grid items-end bg-[rgba(24,18,10,.36)]',
        side ? 'sm:justify-end sm:p-3' : 'sm:place-items-center sm:p-7'
      )}
      {...backdrop}
    >
      <div
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          /* pugo-panel: every dialog in the app is drawn by this one
             component, so marking it here is what carries a scene's own
             material to all of them. */
          'flex w-full flex-col overflow-hidden border border-line bg-surface-1 shadow-[var(--shadow-3)] outline-none',
          'max-h-[92dvh] rounded-t-[var(--radius-xl)] sm:rounded-[var(--radius-xl)]',
          side
            ? 'sm:h-full sm:max-h-none sm:w-[420px] sm:animate-[slide-in-right_var(--t-panel-in)_var(--ease-out)]'
            : 'sm:max-h-[88vh]',
          !side && 'animate-[pop_var(--t-panel-in)_var(--ease-out)]',
          side ? '' : xl ? 'sm:max-w-[940px]' : wide ? 'sm:max-w-[720px]' : 'sm:max-w-[540px]'
        )}
      >
        <header className="flex items-start gap-3 border-b border-line-soft px-5 pt-5 pb-4 sm:px-6">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-[20px] leading-tight font-extrabold tracking-[-.02em] text-ink">{title}</h2>
            {subtitle ? <p className="mt-1 text-[14px] text-mute">{subtitle}</p> : null}
          </div>
          <IconButton label="Close" onClick={onClose}>
            <svg width="15" height="15" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </IconButton>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">{children}</div>
        {footer ? (
          <footer className="flex flex-wrap justify-end gap-2.5 border-t border-line-soft px-5 pt-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))] sm:px-6 sm:pb-4">
            {footer}
          </footer>
        ) : null}
      </div>
      <style>{`@keyframes pop { from { opacity:0; transform: translateY(10px) } to { opacity:1; transform:none } }
        @keyframes slide-in-right { from { opacity:0; transform: translateX(26px) } to { opacity:1; transform:none } }`}</style>
    </div>,
    document.body
  );
}

export function useConfirm() {
  const [state, setState] = useState<{
    title: string; message: string; confirmLabel: string; danger: boolean;
    resolve: (ok: boolean) => void;
  } | null>(null);

  const confirm = useCallback(
    (title: string, message: string, opts?: { confirmLabel?: string; danger?: boolean }) =>
      new Promise<boolean>((resolve) => {
        setState({
          title, message,
          confirmLabel: opts?.confirmLabel ?? 'Confirm',
          danger: !!opts?.danger,
          resolve
        });
      }),
    []
  );

  const dialog = state ? (
    <Modal
      title={state.title}
      onClose={() => { state.resolve(false); setState(null); }}
      footer={
        <>
          <Button kind="ghost" onClick={() => { state.resolve(false); setState(null); }}>Cancel</Button>
          <Button
            data-primary
            kind={state.danger ? 'danger' : 'primary'}
            onClick={() => { state.resolve(true); setState(null); }}
          >
            {state.confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-[14px] text-dim">{state.message}</p>
      <p className="mt-3 text-[12px] text-ghost">
        <Kbd>Enter</Kbd> to {state.confirmLabel.toLowerCase()}, <Kbd>Esc</Kbd> to cancel
      </p>
    </Modal>
  ) : null;

  return { confirm, dialog };
}

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded-[calc(6px*var(--radius-mul,1))] border border-line bg-surface-3 px-1.5 py-0.5 font-mono text-[11px] text-dim">
      {children}
    </kbd>
  );
}

/* ------------------------------------------------------------- toast */

type Toast = { id: number; text: string; kind: 'good' | 'bad' | 'warn' | 'plain'; duration: number };
type ToastApi = (text: string, kind?: Toast['kind']) => void;

const ToastContext = createContext<ToastApi>(() => {});
export const useToast = () => useContext(ToastContext);

/*
 * One notice at a time.
 *
 * Toasts used to stack four deep in the corner, and a failing request that
 * retried put the same error up over and over. Now a new notice replaces the
 * one showing, the same notice again just restarts its timer, and a thin bar
 * drains across the bottom until it goes. Pointing at it pauses the bar.
 * Closing it with X also mutes that exact message until a different one has
 * come along - so a repeating error cannot keep coming back.
 *
 * Small and out of the way: top centre under the title bar, never over the
 * controls you are using, and only the notice itself takes clicks.
 */
export function ToastHost({ children }: { children: ReactNode }) {
  const [item, setItem] = useState<Toast | null>(null);
  const [leaving, setLeaving] = useState(false);
  const muted = useRef<string | null>(null);
  const current = useRef<Toast | null>(null);
  current.current = item;
  const leaveTimer = useRef(0);

  const push = useCallback<ToastApi>((text, kind = 'plain') => {
    const message = String(text || '').trim();
    if (!message) return;
    if (muted.current === message) return;
    muted.current = null;
    window.clearTimeout(leaveTimer.current);
    setLeaving(false);
    setItem({ id: Date.now() + Math.random(), text: message, kind, duration: kind === 'bad' || kind === 'warn' ? 5200 : 3400 });
  }, []);

  const close = useCallback((byHand: boolean) => {
    const showing = current.current;
    if (!showing) return;
    if (byHand) muted.current = showing.text;
    setLeaving(true);
    window.clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(() => {
      setItem((now) => (now && now.id === showing.id ? null : now));
      setLeaving(false);
    }, 180);
  }, []);

  const tone = item?.kind === 'good' ? 'var(--good)' : item?.kind === 'bad' ? 'var(--bad)' : item?.kind === 'warn' ? 'var(--warn)' : 'var(--accent)';
  const icon = item?.kind === 'good' ? 'checkCirc' : item?.kind === 'bad' ? 'alert' : item?.kind === 'warn' ? 'alert' : 'info';

  return (
    <ToastContext.Provider value={push}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed right-5 bottom-[96px] z-[500] flex max-w-[calc(100vw-40px)] justify-end">
          {item ? (
            <div
              role={item.kind === 'bad' ? 'alert' : 'status'}
              aria-live={item.kind === 'bad' ? 'assertive' : 'polite'}
              className={cn(
                'pugo-toast pointer-events-auto relative flex w-[360px] max-w-full items-start gap-3 overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] border border-line bg-surface-2 py-3 pr-2 pl-3 shadow-[0_18px_44px_-14px_rgba(0,0,0,.6)]',
                leaving ? 'pugo-toast-out' : 'pugo-toast-in'
              )}
            >
              <span className="grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))]" style={{ background: 'color-mix(in srgb, ' + tone + ' 16%, transparent)', color: tone }}>
                <Icon name={icon} size={17} />
              </span>
              <span className="min-w-0 flex-1 pt-px">
                <span className="block text-[13.5px] leading-tight font-bold text-ink">
                  {item.kind === 'bad' ? 'Something went wrong' : item.kind === 'warn' ? 'Heads up' : item.kind === 'good' ? 'Done' : 'Pugo'}
                </span>
                <span className="mt-0.5 line-clamp-3 block text-[13px] leading-snug text-dim">{item.text}</span>
              </span>
              <button
                type="button"
                aria-label="Close notice"
                onClick={() => close(true)}
                className="grid h-7 w-7 flex-none place-items-center rounded-[calc(8px*var(--radius-mul,1))] text-mute outline-none transition-colors hover:bg-surface-3 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
              >
                <Icon name="close" size={13} />
              </button>
              <span
                key={item.id}
                aria-hidden
                onAnimationEnd={() => close(false)}
                className="pugo-toast-bar absolute bottom-0 left-0 h-[2px] w-full origin-left"
                style={{ background: tone, animationDuration: item.duration + 'ms', ['--toast-ms' as string]: item.duration + 'ms' }}
              />
            </div>
          ) : null}
        </div>,
        document.body
      )}
      <style>{`
        .pugo-toast-in { animation: pugo-toast-in 220ms cubic-bezier(.2,.8,.2,1) both; }
        .pugo-toast-out { animation: pugo-toast-out 180ms ease-in both; }
        .pugo-toast-bar { animation-name: pugo-toast-bar; animation-timing-function: linear; animation-fill-mode: forwards; }
        .pugo-toast:hover .pugo-toast-bar { animation-play-state: paused; }
        @keyframes pugo-toast-in { from { opacity: 0; transform: translateY(10px) scale(.98); } to { opacity: 1; transform: none; } }
        @keyframes pugo-toast-out { to { opacity: 0; transform: translateX(12px) scale(.98); } }
        @keyframes pugo-toast-bar { from { transform: scaleX(1); } to { transform: scaleX(0); } }
        @media (prefers-reduced-motion: reduce) { .pugo-toast-in, .pugo-toast-out { animation-duration: 1ms; } }
      `}</style>
    </ToastContext.Provider>
  );
}
